import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Sparkles, 
  Rocket, 
  Crown, 
  BarChart3, 
  Palette,
  Zap,
  Check,
  ChevronRight,
  Gift,
  TrendingUp,
  Star,
  ShoppingCart,
  Loader2,
  ExternalLink
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { useTranslation } from 'react-i18next';

const API = process.env.REACT_APP_BACKEND_URL;

export function MonetizationStore({ businessId, onPurchaseComplete }) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('credits');
  const [loading, setLoading] = useState(false);
  const [creditBalance, setCreditBalance] = useState(null);
  const [creditPackages, setCreditPackages] = useState([]);
  const [boostPackages, setBoostPackages] = useState([]);
  const [templatePackages, setTemplatePackages] = useState([]);
  const [analyticsPlans, setAnalyticsPlans] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [processingPurchase, setProcessingPurchase] = useState(false);
  const [taxInfo, setTaxInfo] = useState({ tax_rate: 21, tax_name: 'IVA', reverse_charge: false });

  useEffect(() => {
    fetchData();
  }, [businessId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = { 'X-Business-Id': businessId };
      const [creditsRes, balanceRes, boostRes, templatesRes, analyticsRes, taxRes] = await Promise.all([
        axios.get(`${API}/api/monetization/credits/packages`, { headers }),
        axios.get(`${API}/api/monetization/credits/balance`, { headers }),
        axios.get(`${API}/api/monetization/boost/packages`, { headers }),
        axios.get(`${API}/api/monetization/templates/packages`, { headers }),
        axios.get(`${API}/api/monetization/analytics/plans`, { headers }),
        axios.get(`${API}/api/monetization/tax/my-rate`, { headers }).catch(() => ({ data: { tax_rate: 21, tax_name: 'IVA', reverse_charge: false } }))
      ]);
      
      setCreditPackages(creditsRes.data.packages || []);
      setCreditBalance(balanceRes.data);
      setBoostPackages(boostRes.data.packages || []);
      setTemplatePackages(templatesRes.data.packages || []);
      setAnalyticsPlans(analyticsRes.data.plans || []);
      
      // Set tax info from any response or specific endpoint
      setTaxInfo({
        tax_rate: creditsRes.data.tax_rate || taxRes.data.tax_rate || 21,
        tax_name: creditsRes.data.tax_name || taxRes.data.tax_name || 'IVA',
        reverse_charge: creditsRes.data.reverse_charge || taxRes.data.reverse_charge || false,
        tax_reason: taxRes.data.tax_reason || '',
        country: taxRes.data.country || 'ES'
      });
    } catch (error) {
      console.error('Error fetching monetization data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async (type, packageId, kuponId = null) => {
    setProcessingPurchase(true);
    try {
      let response;
      const origin_url = window.location.origin;
      
      switch (type) {
        case 'credits':
          response = await axios.post(`${API}/api/monetization/credits/purchase`, 
            { package: packageId, origin_url },
            { headers: { 'X-Business-Id': businessId } }
          );
          break;
          
        case 'boost':
          if (!kuponId) {
            toast.error('Selecciona un kupon para destacar');
            setProcessingPurchase(false);
            return;
          }
          response = await axios.post(`${API}/api/monetization/boost/purchase`,
            { kupon_id: kuponId, package: packageId, origin_url },
            { headers: { 'X-Business-Id': businessId } }
          );
          break;
          
        case 'templates':
          response = await axios.post(`${API}/api/monetization/templates/purchase`,
            { package: packageId, origin_url },
            { headers: { 'X-Business-Id': businessId } }
          );
          break;
          
        case 'analytics':
          response = await axios.post(`${API}/api/monetization/analytics/subscribe`,
            { plan: packageId, origin_url },
            { headers: { 'X-Business-Id': businessId } }
          );
          break;
          
        default:
          throw new Error('Tipo de compra no válido');
      }
      
      // Handle response
      if (response.data.checkout_url) {
        // Redirect to Stripe Checkout
        toast.success('Redirigiendo a la pasarela de pago...');
        window.location.href = response.data.checkout_url;
      } else if (response.data.mock) {
        // Mock mode - auto-confirm
        toast.info('Modo demo: confirmando compra automáticamente...');
        await confirmPurchase(type, response.data.purchase_id || response.data.boost_id || response.data.subscription_id);
      } else {
        toast.error('No se pudo crear la sesión de pago');
      }
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al procesar la compra');
      setProcessingPurchase(false);
    }
  };

  const confirmPurchase = async (type, purchaseId) => {
    try {
      const response = await axios.get(`${API}/api/monetization/payment/success`, {
        params: { purchase_id: purchaseId, type },
        headers: { 'X-Business-Id': businessId }
      });
      
      if (response.data.success) {
        toast.success(response.data.message);
        fetchData();
        if (onPurchaseComplete) onPurchaseComplete();
      } else {
        toast.error(response.data.message || 'Error confirmando compra');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error confirmando compra');
    } finally {
      setProcessingPurchase(false);
    }
  };

  // Check for payment success on mount (callback from Stripe)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const monetizationSuccess = urlParams.get('monetization_success');
    const purchaseId = urlParams.get('purchase_id');
    const purchaseType = urlParams.get('type');
    
    if (monetizationSuccess === 'true' && purchaseId && purchaseType) {
      confirmPurchase(purchaseType, purchaseId);
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    
    const monetizationCancelled = urlParams.get('monetization_cancelled');
    if (monetizationCancelled === 'true') {
      toast.info('Compra cancelada');
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const tabs = [
    { id: 'credits', label: 'Créditos', icon: Zap, color: 'text-yellow-500' },
    { id: 'boost', label: 'Destacar', icon: Rocket, color: 'text-purple-500' },
    { id: 'templates', label: 'Plantillas', icon: Palette, color: 'text-pink-500' },
    { id: 'analytics', label: 'Analíticas', icon: BarChart3, color: 'text-blue-500' }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="bg-card rounded-2xl shadow-card border border-border/50 overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 p-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
            <Crown className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">Potencia tu Negocio</h2>
            <p className="text-emerald-100 text-sm">Herramientas premium para crecer más rápido</p>
          </div>
        </div>
        
        {/* Credit Balance Display */}
        {creditBalance && (
          <div className="mt-4 bg-white/10 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-emerald-100 text-sm">Créditos disponibles</p>
                <p className="text-2xl font-bold text-white">
                  {creditBalance.total_available === 'unlimited' ? '∞' : creditBalance.total_available}
                </p>
              </div>
              <div className="text-right">
                <p className="text-emerald-100 text-sm">Plan actual</p>
                <p className="text-lg font-semibold text-white capitalize">{creditBalance.plan}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex border-b border-border overflow-x-auto">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 min-w-[100px] flex items-center justify-center gap-2 py-4 px-3 text-sm font-medium transition-all ${
              activeTab === tab.id
                ? 'border-b-2 border-primary text-primary bg-primary/5'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
            }`}
          >
            <tab.icon className={`w-4 h-4 ${activeTab === tab.id ? tab.color : ''}`} />
            <span className="hidden sm:inline">{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="p-6">
        <AnimatePresence mode="wait">
          {activeTab === 'credits' && (
            <motion.div
              key="credits"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="space-y-4"
            >
              <div className="flex items-center gap-2 mb-4">
                <Zap className="w-5 h-5 text-yellow-500" />
                <h3 className="font-semibold">Comprar Créditos Extra</h3>
              </div>
              <p className="text-sm text-muted-foreground mb-4">
                ¿Te quedaste sin kupones este mes? Compra créditos extra para seguir publicando.
              </p>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {creditPackages.map((pkg) => (
                  <div
                    key={pkg.id}
                    className={`relative bg-muted/30 rounded-xl p-4 border-2 transition-all cursor-pointer hover:border-primary/50 ${
                      pkg.savings_percent > 30 ? 'border-yellow-500/50' : 'border-transparent'
                    } ${processingPurchase ? 'opacity-50 pointer-events-none' : ''}`}
                    onClick={() => !processingPurchase && handlePurchase('credits', pkg.id)}
                    data-testid={`credit-package-${pkg.id}`}
                  >
                    {pkg.savings_percent > 0 && (
                      <div className="absolute -top-2 -right-2 bg-yellow-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                        -{pkg.savings_percent}%
                      </div>
                    )}
                    <div className="text-center">
                      <p className="text-3xl font-bold text-primary">{pkg.credits}</p>
                      <p className="text-xs text-muted-foreground mb-2">créditos</p>
                      <p className="text-lg font-bold">{pkg.price}€</p>
                      <p className="text-xs text-muted-foreground">{pkg.price_per_credit}€/kupon</p>
                      {pkg.tax_amount && (
                        <p className="text-xs text-amber-500 mt-1">IVA incl. ({pkg.tax_rate}%)</p>
                      )}
                      {processingPurchase && (
                        <Loader2 className="w-4 h-4 animate-spin mx-auto mt-2 text-primary" />
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-4 flex items-center gap-1">
                {taxInfo.reverse_charge ? (
                  <span className="text-emerald-500">Inversión del sujeto pasivo: precios netos (0% IVA). Pago seguro con Stripe.</span>
                ) : taxInfo.tax_rate === 0 ? (
                  <span className="text-blue-500">Exportación de servicios: precios netos (exento IVA). Pago seguro con Stripe.</span>
                ) : (
                  <><span className="text-amber-500">*</span> Precios con {taxInfo.tax_name} ({taxInfo.tax_rate}%) incluido. Pago seguro con Stripe.</>
                )}
              </p>
            </motion.div>
          )}

          {activeTab === 'boost' && (
            <motion.div
              key="boost"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="space-y-4"
            >
              <div className="flex items-center gap-2 mb-4">
                <Rocket className="w-5 h-5 text-purple-500" />
                <h3 className="font-semibold">Destacar tu Kupon</h3>
              </div>
              <p className="text-sm text-muted-foreground mb-4">
                Haz que tu kupon aparezca primero y reciba más visibilidad.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {boostPackages.map((pkg) => (
                  <div
                    key={pkg.id}
                    className={`bg-muted/30 rounded-xl p-4 border-2 transition-all ${
                      pkg.id === 'launch' 
                        ? 'border-purple-500/50 bg-purple-500/5' 
                        : 'border-transparent hover:border-primary/30'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h4 className="font-semibold flex items-center gap-2">
                          {pkg.name}
                          {pkg.id === 'launch' && <Star className="w-4 h-4 text-yellow-500" />}
                        </h4>
                        <p className="text-sm text-muted-foreground">{pkg.description}</p>
                      </div>
                      {pkg.savings_percent > 0 && (
                        <span className="bg-green-500/10 text-green-600 text-xs font-bold px-2 py-1 rounded-full">
                          -{pkg.savings_percent}%
                        </span>
                      )}
                    </div>
                    
                    {pkg.features && pkg.features.length > 1 && (
                      <div className="mb-3 space-y-1">
                        {pkg.features.map((feature, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-xs text-muted-foreground">
                            <Check className="w-3 h-3 text-green-500" />
                            <span>{feature.replace('_', ' ')}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    <div className="flex items-center justify-between">
                      <p className="text-xl font-bold">{pkg.price}€</p>
                      <Button
                        size="sm"
                        className={pkg.id === 'launch' ? 'bg-purple-600 hover:bg-purple-700' : ''}
                        onClick={() => toast.info('Selecciona un kupon desde tu lista de kupones')}
                      >
                        Destacar
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {activeTab === 'templates' && (
            <motion.div
              key="templates"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="space-y-4"
            >
              <div className="flex items-center gap-2 mb-4">
                <Palette className="w-5 h-5 text-pink-500" />
                <h3 className="font-semibold">Plantillas Premium</h3>
              </div>
              <p className="text-sm text-muted-foreground mb-4">
                Diseños profesionales para que tus kupones destaquen.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {templatePackages.map((pkg) => (
                  <div
                    key={pkg.id}
                    className={`bg-muted/30 rounded-xl p-4 border-2 transition-all ${
                      pkg.id === 'unlimited' 
                        ? 'border-pink-500/50 bg-pink-500/5' 
                        : 'border-transparent hover:border-primary/30'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h4 className="font-semibold flex items-center gap-2">
                          {pkg.name}
                          {pkg.id === 'unlimited' && <Crown className="w-4 h-4 text-yellow-500" />}
                        </h4>
                        <p className="text-sm text-muted-foreground">
                          {pkg.template_count === 'unlimited' 
                            ? 'Acceso a todas las plantillas' 
                            : `${pkg.template_count} plantillas`}
                        </p>
                      </div>
                      {pkg.savings_percent > 0 && (
                        <span className="bg-green-500/10 text-green-600 text-xs font-bold px-2 py-1 rounded-full">
                          -{pkg.savings_percent}%
                        </span>
                      )}
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xl font-bold">{pkg.price}€</p>
                        {pkg.type === 'subscription' && (
                          <p className="text-xs text-muted-foreground">/año</p>
                        )}
                      </div>
                      <Button
                        size="sm"
                        className={pkg.id === 'unlimited' ? 'bg-pink-600 hover:bg-pink-700' : ''}
                        onClick={() => handlePurchase('templates', pkg.id)}
                        disabled={processingPurchase}
                        data-testid={`template-buy-${pkg.id}`}
                      >
                        {processingPurchase ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Comprar'}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {activeTab === 'analytics' && (
            <motion.div
              key="analytics"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="space-y-4"
            >
              <div className="flex items-center gap-2 mb-4">
                <BarChart3 className="w-5 h-5 text-blue-500" />
                <h3 className="font-semibold">Dashboard de Analíticas</h3>
              </div>
              <p className="text-sm text-muted-foreground mb-4">
                Conoce a tus clientes y optimiza tus ofertas con datos en tiempo real.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {analyticsPlans.map((plan) => (
                  <div
                    key={plan.id}
                    className={`bg-muted/30 rounded-xl p-4 border-2 transition-all ${
                      plan.id === 'pro' 
                        ? 'border-blue-500/50 bg-blue-500/5 scale-105' 
                        : 'border-transparent hover:border-primary/30'
                    }`}
                  >
                    {plan.id === 'pro' && (
                      <div className="text-center mb-2">
                        <span className="bg-blue-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                          POPULAR
                        </span>
                      </div>
                    )}
                    
                    <div className="text-center mb-4">
                      <h4 className="font-semibold">{plan.name}</h4>
                      <p className="text-2xl font-bold mt-2">{plan.price}€<span className="text-sm font-normal text-muted-foreground">/mes</span></p>
                    </div>
                    
                    <div className="space-y-2 mb-4">
                      {plan.features.map((feature, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-sm">
                          <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                          <span className="capitalize">{feature.replace('_', ' ')}</span>
                        </div>
                      ))}
                    </div>
                    
                    <Button
                      className={`w-full ${plan.id === 'pro' ? 'bg-blue-600 hover:bg-blue-700' : ''}`}
                      onClick={() => handlePurchase('analytics', plan.id)}
                      disabled={processingPurchase}
                      data-testid={`analytics-subscribe-${plan.id}`}
                    >
                      {processingPurchase ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Suscribirse'}
                    </Button>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      
      {/* Footer */}
      <div className="bg-muted/30 px-6 py-4 border-t border-border">
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Gift className="w-4 h-4" />
            <span>Pago seguro con Stripe</span>
          </div>
          <div className="flex items-center gap-2 text-muted-foreground">
            <TrendingUp className="w-4 h-4" />
            <span>+47% más reservas con boost</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MonetizationStore;
